import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { SlotLockManager } from "./SlotLockManager/SlotLockManager";
import { TimeSlotBookingDetailsStepNode$key } from "./__generated__/TimeSlotBookingDetailsStepNode.graphql";
import {
  GroupSizeControl,
  CourseLayoutControl,
  TransportationTypeControl,
} from "./controls";
import { CourseAndDurationControl } from "./controls/CourseAndDurationControl/CourseAndDurationControl";

type Props = {
  nodeRef: TimeSlotBookingDetailsStepNode$key | null;
  memberId: string;
  onClose: () => void;
  initiallyLockedSlotIds?: readonly string[];
};

export const TimeSlotBookingDetailsStep = ({
  nodeRef,
  memberId,
  onClose,
  initiallyLockedSlotIds,
}: Props) => {
  const node = useFragment(TimeSlotBookingDetailsStepFragmentString, nodeRef);
  const isMultiSlot = node?.type === "MULTI";
  const isPrivate = node?.agenda?.facility?.club?.supportsPrivate ?? false;

  const isMultiPrivate = isMultiSlot && isPrivate;

  const isMobileLayout = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: false,
  });

  return (
    <VStack
      backgroundColor="surface-secondary"
      paddingTop={2}
      flex={1}
      maxHeight={isMobileLayout ? undefined : 500}
    >
      <SlotLockManager
        onClose={onClose}
        initiallyLockedSlotIds={initiallyLockedSlotIds}
      />
      <VStack backgroundColor="surface-secondary" paddingBottom={6}>
        {!isMultiPrivate ? <GroupSizeControl nodeRef={node ?? null} /> : null}
        {isMultiSlot ? (
          <CourseAndDurationControl
            memberId={memberId}
            slotId={node?.id ?? ""}
            agendaId={node?.agenda?.id ?? ""}
            startTime={node?.time ?? ""}
          />
        ) : (
          <>
            <CourseLayoutControl nodeRef={node ?? null} />
            <TransportationTypeControl nodeRef={node ?? null} />
          </>
        )}
      </VStack>
    </VStack>
  );
};

export const TimeSlotBookingDetailsStepFragmentString = graphql`
  fragment TimeSlotBookingDetailsStepNode on TimeSlot
  @argumentDefinitions(
    memberId: { type: "ID", defaultValue: null }
    membershipTypeId: { type: "ID", defaultValue: null }
  ) {
    id
    type
    time
    course {
      courseBucket {
        id
      }
    }
    agenda {
      id
      facility {
        club {
          supportsPrivate
        }
      }
    }
    ...GroupSizeControlNode
    ...CourseLayoutControlNode
    ...TransportationTypeControlNode
      @arguments(memberId: $memberId, membershipTypeId: $membershipTypeId)
  }
`;
